/**
 * The transport desk (M40, ADR-0066). The server's `transport_booking:write`
 * capability (`support` and `amtc_admin`, owner Q17) is the real guard; the
 * role list here only decides what the admin shows and asks for.
 */
import { apiFetch } from './client'

const BASE = '/api/v1/admin/transport-bookings'

/** The roles holding `transport_booking:write`. Keep in step with the server's
 *  capability table (`app/lib/permissions.py`). */
export const TRANSPORT_DESK_ROLES = ['support', 'amtc_admin'] as const

export function isTransportDesk(role: string | undefined): boolean {
  return (TRANSPORT_DESK_ROLES as readonly string[]).includes(role ?? '')
}

/** Where the desk works (M40-T12). */
export const TRANSPORT_DESK_PATH = '/admin/transport-bookings'

/** The queue, filtered to the bookings past their cut-off (T03's view). */
export const PAST_CUTOFF_QUEUE_PATH =
  '/admin/transport-bookings?view=past_cutoff'

/** Everything this module caches starts with this, so one invalidation after
 *  a staff action refreshes the queue, the booking and the dashboard's count. */
export const TRANSPORT_DESK_KEY = ['admin', 'transport-bookings'] as const

/** M40-T07: how many paid bookings carry each flag. One booking with two
 *  flags counts in both. */
export type TransportAttention = {
  past_cutoff: number
  over_capacity: number
  return_requested: number
}

export const TRANSPORT_ATTENTION_KEY = [
  ...TRANSPORT_DESK_KEY,
  'attention',
] as const

/** GET /api/v1/admin/transport-bookings/attention */
export async function fetchTransportAttention(
  token: string | null,
): Promise<TransportAttention> {
  const body = await apiFetch<{ data: TransportAttention }>(
    `${BASE}/attention`,
    {
      token,
    },
  )
  return body.data
}

// ------------------------------------------------- the desk's queue (M40-T12)

/** The queue's views (T03). `all` is the page's own name for no view. */
export const DESK_VIEWS = [
  { key: 'next_48h_unassigned', label: 'Next 48 hours, not assigned' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'past_cutoff', label: 'Past cut-off' },
  { key: 'all', label: 'All' },
] as const
export type DeskView = (typeof DESK_VIEWS)[number]['key']
export const DEFAULT_DESK_VIEW: DeskView = 'next_48h_unassigned'

/** `?view=` from the URL, over the default: the dashboard banner links to
 *  `past_cutoff` (T07). Anything unknown is the default. */
export function deskViewFrom(search: URLSearchParams): DeskView {
  const view = search.get('view')
  const known = DESK_VIEWS.find((v) => v.key === view)
  return known ? known.key : DEFAULT_DESK_VIEW
}

/** Paid statuses, in journey order (`services/transport/desk.py`). */
export const DESK_STATUSES = [
  'confirmed',
  'driver_assigned',
  'picked_up',
  'arrived',
  'return_requested',
  'return_picked_up',
  'completed',
  'cancelled_by_customer',
  'cannot_fulfil',
  'no_show',
] as const
export type DeskStatus = (typeof DESK_STATUSES)[number]

const STATUS_LABELS: Record<DeskStatus, string> = {
  confirmed: 'Confirmed, no driver',
  driver_assigned: 'Driver assigned',
  picked_up: 'Picked up',
  arrived: 'Arrived',
  return_requested: 'Return requested',
  return_picked_up: 'Return picked up',
  completed: 'Completed',
  cancelled_by_customer: 'Cancelled by customer',
  cannot_fulfil: 'Cannot fulfil',
  no_show: 'No show',
}

export function deskStatusLabel(status: string): string {
  return STATUS_LABELS[status as DeskStatus] ?? status.replace(/_/g, ' ')
}

export type DeskQueueItem = {
  id: string
  reference: string
  service: string
  status: string
  pickup_at: string
  pickup_area: string
  hospital: string
  booker_name: string
  booker_phone_masked: string
  past_cutoff: boolean
  over_capacity: boolean
  return_requested: boolean
  hospital_informed: boolean
}

export type DeskQueue = { data: DeskQueueItem[]; total: number }

export type DeskQueueFilters = {
  view: DeskView
  status: string | null
  dateFrom: string
  dateTo: string
  offset: number
}

/** One page of the queue (the server allows up to 100). */
export const DESK_PAGE_SIZE = 50

export const DESK_QUEUE_KEY = [...TRANSPORT_DESK_KEY, 'queue'] as const

/** GET /api/v1/admin/transport-bookings: past cut-off first, then over
 *  capacity, then return requested, then by pickup time (T03). */
export async function fetchDeskQueue(
  token: string | null,
  f: DeskQueueFilters,
): Promise<DeskQueue> {
  const params = new URLSearchParams()
  if (f.view !== 'all') params.set('view', f.view)
  if (f.status) params.set('status', f.status)
  if (f.dateFrom) params.set('date_from', f.dateFrom)
  if (f.dateTo) params.set('date_to', f.dateTo)
  params.set('limit', String(DESK_PAGE_SIZE))
  params.set('offset', String(f.offset))
  return apiFetch<DeskQueue>(`${BASE}?${params.toString()}`, { token })
}

// ------------------------------------------------------------- one booking

export type DeskNote = {
  id: string
  body: string
  author_name: string | null
  created_at: string
}

export type DeskRefundDue = {
  id: string
  reason: string
  amount_pesewas: number
  created_at: string
  issued_at: string | null
  issued_reference: string | null
}

/** What `allowed_actions` may hold (`journey.ACTIONS`). */
export type DeskActionName =
  | 'assign'
  | 'send_driver_details'
  | 'details_given'
  | 'assign_return'
  | 'status'
  | 'hospital_informed'
  | 'no_show'
  | 'cannot_fulfil'
  | 'cancel'
  | 'revoke_link'

export type DeskBooking = {
  id: string
  reference: string
  status: string
  service: string
  mobility: string
  uses_oxygen: boolean
  booker_first_name: string
  booker_last_name: string
  booker_phone: string
  passenger_is_booker: boolean
  passenger_name: string | null
  passenger_age: number | null
  passenger_phone: string | null
  equipment: string[]
  companion: string
  driver_notes: string | null
  pickup_area: string
  pickup_address: string
  pickup_landmark: string | null
  pickup_lat: number | null
  pickup_lng: number | null
  hospital: string
  zone: number
  appointment_at: string | null
  pickup_at: string
  journey: string | null
  return_mode: string | null
  return_pickup_at: string | null
  hire_length: string | null
  hire_stops: string | null
  fare_lines: Array<Record<string, unknown>>
  total_pesewas: number
  payment_method: string | null
  confirmed_at: string | null
  driver_name: string | null
  driver_phone: string | null
  vehicle_plate: string | null
  attendant_name: string | null
  driver_details_given_at: string | null
  return_driver_name: string | null
  return_driver_phone: string | null
  return_vehicle_plate: string | null
  hospital_informed_at: string | null
  return_requested_at: string | null
  over_capacity: boolean
  past_cutoff: boolean
  cancel_reason: string | null
  link_active: boolean
  created_at: string
  /** Send back exactly as received, as `if_updated_at`: it carries
   *  microseconds, which a JS `Date` would drop, making every action stale. */
  updated_at: string
  allowed_actions: DeskActionName[]
  next_status: string | null
  sms_live: boolean
  notes: DeskNote[]
  refunds_due: DeskRefundDue[]
}

export function deskBookingKey(id: string) {
  return [...TRANSPORT_DESK_KEY, 'booking', id] as const
}

/** GET /api/v1/admin/transport-bookings/{id}: phones in full (§E.8). */
export async function fetchDeskBooking(
  token: string | null,
  id: string,
): Promise<DeskBooking> {
  const body = await apiFetch<{ data: DeskBooking }>(`${BASE}/${id}`, {
    token,
  })
  return body.data
}

/** One staff action (T04), without `if_updated_at`: `actOnBooking` adds it. */
export type DeskAction =
  | {
      action: 'assign'
      driver_name: string
      driver_phone: string
      vehicle_plate: string
      attendant_name?: string
    }
  | { action: 'details_given' }
  | {
      action: 'assign_return'
      return_driver_name: string
      return_driver_phone: string
      return_vehicle_plate: string
    }
  | { action: 'status'; status: string }
  | { action: 'hospital_informed' }
  | { action: 'no_show'; reason: string }
  | { action: 'cannot_fulfil'; reason: string }
  | { action: 'cancel'; reason: string; refund_pesewas: number }
  | { action: 'revoke_link' }

/** PATCH /api/v1/admin/transport-bookings/{id}. Answers the booking as it now
 *  is. A stale `updatedAt` is 409 `concurrent_modification`. */
export async function actOnBooking(
  token: string | null,
  id: string,
  updatedAt: string,
  action: DeskAction,
): Promise<DeskBooking> {
  const body = await apiFetch<{ data: DeskBooking }>(`${BASE}/${id}`, {
    token,
    method: 'PATCH',
    body: { ...action, if_updated_at: updatedAt },
  })
  return body.data
}

/** POST …/{id}/send-driver-details: 409 `sms_not_live` until SMS is live. */
export async function sendDriverDetails(
  token: string | null,
  id: string,
): Promise<DeskBooking> {
  const body = await apiFetch<{ data: DeskBooking }>(
    `${BASE}/${id}/send-driver-details`,
    { token, method: 'POST' },
  )
  return body.data
}

/** The server's limit on one note (`AddDeskNote`). */
export const DESK_NOTE_MAX = 1000

/** POST …/{id}/notes: append-only, never shown to the customer. */
export async function addDeskNote(
  token: string | null,
  id: string,
  text: string,
): Promise<DeskNote> {
  const body = await apiFetch<{ data: DeskNote }>(`${BASE}/${id}/notes`, {
    token,
    method: 'POST',
    body: { body: text },
  })
  return body.data
}

// ------------------------------------------------------- refunds due (T05)

/** The roles holding `transport_refund:mark_issued` (owner Q17). */
export const REFUND_MANAGER_ROLES = ['amtc_admin'] as const

export function isRefundManager(role: string | undefined): boolean {
  return (REFUND_MANAGER_ROLES as readonly string[]).includes(role ?? '')
}

export type RefundDueItem = DeskRefundDue & {
  booking_id: string
  reference: string
}

export type RefundDueList = { data: RefundDueItem[]; total: number }

export const REFUNDS_KEY = ['admin', 'transport-refunds'] as const

const REFUNDS = '/api/v1/admin/transport-refunds'

/** One page of refunds (the server's cap). */
export const REFUNDS_PAGE_SIZE = 100

/** GET /api/v1/admin/transport-refunds, oldest first. */
export async function fetchRefundsDue(
  token: string | null,
  issued: boolean,
): Promise<RefundDueList> {
  return apiFetch<RefundDueList>(
    `${REFUNDS}?issued=${String(issued)}&limit=${REFUNDS_PAGE_SIZE}`,
    { token },
  )
}

/** The payment side's reference, as the server accepts it. */
export const ISSUED_REFERENCE = /^[A-Za-z0-9._/-]{1,100}$/

/** POST …/{id}/issued with the reference and the day: once only (409
 *  `already_issued`). */
export async function markRefundIssued(
  token: string | null,
  id: string,
  issuedReference: string,
  issuedOn: string,
): Promise<RefundDueItem> {
  const body = await apiFetch<{ data: RefundDueItem }>(
    `${REFUNDS}/${id}/issued`,
    {
      token,
      method: 'POST',
      body: { issued_reference: issuedReference, issued_on: issuedOn },
    },
  )
  return body.data
}
